/* eslint-disable jsx-a11y/anchor-is-valid */

import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import type { InferType } from 'yup'
import { object, string } from 'yup'

import { EnumGlobalRoutes } from '@/app/router/routerConstants'
import Button from '@/compoments/common/Button'
import Input from '@/compoments/common/Input'
import { setUser } from '@/features/auth/authSlice'
import { useLazyGetMeQuery, useLoginMutation } from '@/services/auth'

const schema = object({
    password: string().required('Password is required'),
    email: string().email('Invalid email format').required('Email is required'),
    appType: string()
        .oneOf(['admin', 'web', 'technician', 'customer'])
        .default('web'),
})

type FormType = InferType<typeof schema>

const LoginPage = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [login, { isLoading: isLoginLoading }] = useLoginMutation()
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<FormType>({
        mode: 'onChange',
        resolver: yupResolver(schema),
    })
    const [triggerGetMe] = useLazyGetMeQuery()

    const onSubmit = async (data: FormType) => {
        try {
            const res = await login(data).unwrap()
            if (!res?.success) {
                toast.error(
                    res?.errors?.[0]?.msg ||
                        res?.message ||
                        'Something went wrong',
                )
                return
            }
            const meResponse = await triggerGetMe().unwrap()

            if (meResponse?.data) {
                console.log('login res', meResponse.data)
                dispatch(setUser(meResponse.data))

                toast.success('Login successfully!')
                navigate(EnumGlobalRoutes.DASHBOARD)
            } else {
                toast.error('Something went wrong')
            }
        } catch (error: any) {
            if (error?.data?.message) {
                toast.error(error.data.message)
            } else {
                toast.error('An error occurred during login')
            }
            console.error('Login error:', error)
        }
    }

    return (
        <form
            method="post"
            onSubmit={handleSubmit(onSubmit)}
            className="form"
        >
            <div className="form-header form-header-col">
                <div className="heading-block text-center">
                    <h1 className="login-box-msg mb-22">Login</h1>
                    <p className="text-description text--md mb-42">
                        Enter your credentials to access your account.
                    </p>
                </div>
            </div>
            <div className="form-body ">
                <div className="row mb-22">
                    <div className="col-12 mb-22">
                        <Input
                            label="Email address"
                            className="input input--outline  w-full"
                            type="text"
                            placeholder=""
                            error={errors.email?.message}
                            {...register('email')}
                        />
                    </div>
                    <div className="col-12 mb-22">
                        <Input
                            label="Password"
                            afterLabel={
                                <Link
                                    to={'/forgot-password'}
                                    className="btn btn-link text--sm"
                                >
                                    <span className="">Forgot Password?</span>
                                </Link>
                            }
                            className="input input--outline  w-full"
                            type="password"
                            togglePasswordShow
                            placeholder=""
                            error={errors.password?.message}
                            {...register('password')}
                        />
                    </div>
                    <div className="col-12 ">
                        <label className="custom-checkbox">
                            <input
                                type="checkbox"
                                className="custom-checkbox__input"
                            />
                            <span className="custom-checkbox__input-fake">
                                <span className="icon-ico-check"></span>
                            </span>
                            <span className="custom-checkbox__label">
                                Remember Me
                            </span>
                        </label>
                    </div>
                </div>
            </div>
            <div className="form-footer form-footer-col mb-24">
                <div className="items-12 flex flex-col">
                    <Button
                        loading={isLoginLoading}
                        type="submit"
                        className="btn btn--primary-gradient btn--lg w-full fullRadius"
                        disabled={isLoginLoading}
                    >
                        <span>Login</span>
                    </Button>
                </div>
            </div>
            <p className="text-description text--xs gap-10 flex justify-center">
                Do not have an account?
                <a
                    href="#"
                    className="color-primary fw-500"
                >
                    Create Account
                </a>
            </p>
        </form>
    )
}

export default LoginPage
